import { Client, gql } from "urql";
import { CicTypeEnumDescriptions, NaceClassDescriptions } from "../../types.generated";
import { FundParseFormValues } from "./fundParseDefaults";
import { resolveFundParseDate, fundParsePeriodCode } from "./fundParseDateUtils";
import { buildExposureChartsSummary } from "./fundParseExposureChartsBuilder";
import {
    createEsgInputRows,
    esgInputToXlsx,
    exportLookthroughFiles,
    rowsToConsolidatedTptXlsx
} from "./fundParseLookthroughExport";
import { buildOutputManifest, jsonToOutputFile, textToBase64 } from "./fundParseOutputManifestBuilder";
import { cicBucket } from "./fundParseCicClassification";
import {
    buildPositionMappingSummary,
    enrichPositionsWithTptNav
} from "./fundParsePositionMappingBuilder";
import { reportPayloadToTptRecords } from "./fundParseReportPayload";
import { FundParseOutputFile } from "./fundParseTypes";
import { FundParsePositionRecord } from "./fundParseTptTypes";
import { POSITION_MAPPINGS_FILENAME, FUND_PARSE_UNKNOWN_CIC_WARNING } from "./fundParseUtils";
import { EXPOSURE_CHARTS_FILENAME } from "./fundParseExposureChartUtils";
import { GET_TPT_REPORT_SUMMARY } from "./fundParseQueries";

const GET_TPT_REPORT_PAYLOAD = gql`
    query fundParseTptReportPayload($_id: GraphQLObjectId!) {
        report(_id: $_id) {
            _id
            clientId
            clientName
            name
            date
            data
            base64
        }
    }
`;

const GET_FUND_PARSE_POSITIONS = gql`
    query fundParsePositions(
        $clientIds: [GraphQLObjectId!]
        $endDate: GraphQLDateString
        $groupPositionsBy: PositionSelectorType = AccountId
        $filterZeroPositions: Boolean = true
        $lookThrough: Boolean = false
        $excludeCollateral: Boolean = true
        $includeAllCashAccounts: Boolean = false
    ) {
        positions(
            filter: { clientIds: $clientIds, endDate: $endDate }
            groupPositionsBy: $groupPositionsBy
            filterZeroPositions: $filterZeroPositions
            lookThrough: $lookThrough
            excludeCollateral: $excludeCollateral
            includeAllCashAccounts: $includeAllCashAccounts
        ) {
            quantity
            instrument {
                type
                category
                cic
                currency
                modelType
                isin
                name
                longName
                issuer {
                    name
                    longName
                    legalEntityIdentifier
                    naceDescription
                }
                valuations(date: $endDate) {
                    price
                }
            }
        }
    }
`;

function cicTypeDescription(cic: string | null | undefined): string {
    if (!cic || cic.length < 2) {
        return "";
    }
    const cicTypeKey = "XX" + cic.slice(-2);
    const raw = CicTypeEnumDescriptions[cicTypeKey as keyof typeof CicTypeEnumDescriptions] || "";
    const twoDigitCode = cic.slice(-2);
    if (raw.endsWith(twoDigitCode)) {
        return raw.slice(0, -twoDigitCode.length).trim();
    }
    return raw;
}

function positionInstrumentKey(instrument: {
    isin?: string | null;
    name?: string | null;
}): string {
    const isin = instrument.isin?.trim();
    if (isin) {
        return isin;
    }
    const name = instrument.name?.trim();
    if (name) {
        return name;
    }
    throw new Error("Position instrument is missing both ISIN and name.");
}

function consolidatePositions(
    positions: Array<{
        quantity: number;
        instrument: {
            modelType?: string | null;
            cic?: string | null;
            isin?: string | null;
            name?: string | null;
            longName?: string | null;
            issuer?: {
                name?: string | null;
                longName?: string | null;
                legalEntityIdentifier?: string | null;
                naceDescription?: string | null;
            } | null;
            valuations?: { price?: number | null } | null;
        };
    }>
): FundParsePositionRecord[] {
    const consolidated = new Map<string, FundParsePositionRecord>();

    for (const position of positions) {
        const instrument = position.instrument;
        if (instrument.modelType !== "FundExternal" && instrument.modelType !== "FundInternal") {
            continue;
        }

        const instrumentKey = positionInstrumentKey(instrument);
        const quantity = position.quantity;
        const valuationPrice = instrument.valuations?.price ?? 0;
        const valuation = quantity * valuationPrice;
        const cicSuffix = cicTypeDescription(instrument.cic ?? null);
        const cic = (String(instrument.cic ?? "") + (cicSuffix ? " " + cicSuffix : "")).trim();

        const existing = consolidated.get(instrumentKey);
        if (existing) {
            existing["Quantity Held"] += quantity;
            existing.Valuation += valuation;
            continue;
        }

        consolidated.set(instrumentKey, {
            ISIN: instrument.isin || instrumentKey,
            "Fund Shareclass Name": instrument.longName || instrument.name || "",
            CIC: cic,
            "Fund Name": instrument.issuer?.longName || instrument.issuer?.name || "",
            LegalEntityIdentifier: instrument.issuer?.legalEntityIdentifier ?? null,
            NACE: instrument.issuer?.naceDescription ?? null,
            "Quantity Held": quantity,
            "Valuation Price": valuationPrice,
            Valuation: valuation
        });
    }

    return Array.from(consolidated.values()).sort((left, right) =>
        (left.CIC + left["Fund Shareclass Name"]).localeCompare(right.CIC + right["Fund Shareclass Name"])
    );
}

export interface FundParseFromReportResult {
    outputFiles: FundParseOutputFile[];
    consoleLines: string[];
}

function validateLoadFromReport(formValues: FundParseFormValues): string | null {
    if (!formValues.client_id) {
        return "Client ID is required.";
    }
    if (!formValues.position_client_ids.length) {
        return "At least one position client ID is required.";
    }
    return null;
}

export async function processFundParseFromSavedReport(
    gqlClient: Client,
    formValues: FundParseFormValues
): Promise<FundParseFromReportResult> {
    const validationError = validateLoadFromReport(formValues);
    if (validationError) {
        throw new Error(validationError);
    }

    const valuationDate = resolveFundParseDate(formValues.date);
    const periodCode = fundParsePeriodCode(valuationDate);
    const consoleLines: string[] = [];

    const summaryResult = await gqlClient
        .query(
            GET_TPT_REPORT_SUMMARY,
            {
                clientId: formValues.client_id,
                date: valuationDate
            },
            { requestPolicy: "network-only" }
        )
        .toPromise();

    if (summaryResult.error) {
        throw new Error(summaryResult.error.message);
    }

    const reportSummary = summaryResult.data?.reports?.[0];
    if (!reportSummary?._id) {
        throw new Error("No TPT report found for client on " + valuationDate + ".");
    }

    const payloadResult = await gqlClient
        .query(GET_TPT_REPORT_PAYLOAD, { _id: reportSummary._id }, { requestPolicy: "network-only" })
        .toPromise();

    if (payloadResult.error) {
        throw new Error(payloadResult.error.message);
    }

    const report = payloadResult.data?.report;
    if (!report) {
        throw new Error("Could not load TPT report " + reportSummary._id + ".");
    }

    const tptRows = reportPayloadToTptRecords(report);
    consoleLines.push("Loaded TPT report: Client: " + report.clientName + ", Date " + report.date + ".");
    const unknownCicCount = tptRows.filter(
        (row) => cicBucket(String(row["12_CIC_code_of_the_instrument"] ?? "")) === "unknown"
    ).length;
    if (unknownCicCount > 0) {
        consoleLines.push(FUND_PARSE_UNKNOWN_CIC_WARNING);
    }

    const positionsResult = await gqlClient
        .query(
            GET_FUND_PARSE_POSITIONS,
            {
                clientIds: formValues.position_client_ids,
                endDate: valuationDate
            },
            { requestPolicy: "network-only" }
        )
        .toPromise();

    if (positionsResult.error) {
        throw new Error(positionsResult.error.message);
    }

    const posRecords = consolidatePositions(positionsResult.data?.positions || []);
    const enrichedPositions = enrichPositionsWithTptNav(posRecords, tptRows);
    const mappingSummary = buildPositionMappingSummary(tptRows, enrichedPositions, valuationDate, true);
    const exposureSummary = buildExposureChartsSummary(tptRows, valuationDate);

    const outputFiles: FundParseOutputFile[] = [
        jsonToOutputFile(POSITION_MAPPINGS_FILENAME, mappingSummary),
        jsonToOutputFile(EXPOSURE_CHARTS_FILENAME, exposureSummary)
    ];

    if (formValues.create_report_xlsx) {
        outputFiles.push(rowsToConsolidatedTptXlsx(tptRows, periodCode + "_ConsolidatedTPT.xlsx"));
    }

    if (formValues.create_lookthrough_csvs) {
        const lookthrough = exportLookthroughFiles(tptRows, NaceClassDescriptions as Record<string, string>, {
            period: periodCode,
            createInspectionXlsx: formValues.create_report_xlsx
        });
        outputFiles.push(...lookthrough.files);
        consoleLines.push(
            "Look-through export complete: bond=" +
                String(lookthrough.stats.bond) +
                ", equity=" +
                String(lookthrough.stats.equity) +
                ", cash=" +
                String(lookthrough.stats.cash) +
                ", derivative=" +
                String(lookthrough.stats.derivative) +
                ", unknown_cic=" +
                String(lookthrough.stats.unknown_cic) +
                "."
        );

        if (formValues.create_esg_input_xlsx) {
            const esgCandidates = tptRows.filter(
                (row) =>
                    String(row["12_CIC_code_of_the_instrument"] ?? "")
                        .trim()
                        .length > 0
            );
            const esgRows = createEsgInputRows(tptRows);
            const expectedRows = lookthrough.stats.bond + lookthrough.stats.equity;
            if (esgRows.length > expectedRows) {
                consoleLines.push(
                    "Warning: ESG candidate row count (" +
                        esgRows.length +
                        ") exceeds bond+equity look-through rows (" +
                        expectedRows +
                        ")."
                );
            }
            if (esgCandidates.length > 0 || esgRows.length > 0) {
                outputFiles.push(esgInputToXlsx(esgRows, periodCode + "_ESG_input.xlsx"));
            }
        }
    }

    const processingLog = [
        "Fund parse report processing log",
        "Generated at: " + new Date().toISOString().replace("T", " ").slice(0, 19),
        "Client: " + report.clientName,
        "Valuation date: " + valuationDate,
        "TPT rows: " + tptRows.length,
        "Client fund positions: " + posRecords.length
    ].join("\n");
    outputFiles.push({
        fileName: periodCode + "_ProcessingLog.txt",
        base64: textToBase64(processingLog),
        mimeType: "text/plain"
    });

    const finalized = await buildOutputManifest(outputFiles, valuationDate);
    return {
        outputFiles: finalized.files,
        consoleLines
    };
}

export function validateLoadFromSavedReport(formValues: FundParseFormValues): string | null {
    return validateLoadFromReport(formValues);
}
